/* Craft rail rotation.
 *
 * The rail at the foot of the product page led with the shopper's strongest
 * craft and never moved, so a browser whose memory said Ikat saw Ikat on every
 * page. Bed, 3 Oct 2026: "keep it personalised - just keep rotating as per
 * persoal choices", and the order he asked for is "Andhor - then second best
 * anchor - then third best - then fourth best - so on - and all with measured
 * partners".
 *
 * ── WHY IT WRAPS RATHER THAN EDITS ──────────────────────────────────────────
 *
 * craft-picks.js already walks the ranking and takes the first craft that is
 * eligible -- not on this page, not dismissed, with a live collection. Rotating
 * the RANKING therefore rotates the anchor, and the measured partner follows on
 * its own, because partnerCraft() is derived from whichever anchor won. Nothing
 * in the selection, dismissal, affinity or rendering logic has to change.
 *
 * It is also the only safe way to ship this. craft-picks.js is 19,326 bytes and
 * a theme file is pushed by retyping it into a GraphQL document capped at 16,384
 * characters -- so an edited copy cannot be sent at all, and hand-escaping 19KB
 * into a variable is precisely how a transcription error gets into a file nobody
 * then re-reads.
 *
 * ── THE CURSOR ──────────────────────────────────────────────────────────────
 *
 * One step per product-page view, held in localStorage. Not derived from the
 * product id: an id gives variety but not sequence, and the order is the point.
 *
 * It advances on ARRIVAL, when this script runs, and the rail reads the rotated
 * ranking afterwards. So a refresh or a scroll back shows the same rail rather
 * than jumping to the next one, and only a genuine new page view moves it.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ────────────────────────────────────────
 *
 * Nothing for a shopper with no memory. craft-picks.js exits when the total
 * score is under its MIN_SCORE, and that is still a blank rail on a first visit.
 * The agreed design tops that up with generic kinds -- a craft rail, Just
 * landed, Bestselling crafts -- and the collections for those DO exist, built
 * and kept populated by CareOS off the COS_ tags (cos-best-saree, cos-new-kurta
 * and their siblings, per product type). Wiring them needs craft-picks.js to
 * render a non-craft entry, which is the edit the size cap above forbids, so the
 * cold path is left exactly as it was rather than half-wired.
 */
(function () {
  'use strict';

  /*
   * ── AN EXPLICIT CHOICE LASTS A SESSION, NOT FOREVER ────────────────────────
   *
   * craft-picks.js honours sjt_craft_chosen_v1 ahead of everything -- "You asked
   * for Ikat" outranks history, the MIN_SCORE gate and any dismissal. That is
   * right while somebody is asking. It was wrong afterwards: the key never
   * expired, so one Ikat filter pinned the rail indefinitely. Measured on Bed's
   * own browser, the pin had survived long enough that his craft memory read
   * Ajrakh 66.5 against Ikat 1.0 and the rail still said Ikat on every page.
   *
   * Bed, 4 Oct 2026, on what it should do instead: "honour it for a session,
   * then return to rotation".
   *
   * IT LIVES HERE, not in craft-picks.js, for two reasons. This file already
   * runs first -- it is loaded before craft-picks.js precisely so the rotation
   * is in place before the rail reads anything -- so an expired pin is gone
   * before it can be honoured. And craft-picks.js is 15,925 bytes against a
   * 16,384-character push cap; it has no room left for a feature.
   */
  var CHOSEN_KEY = 'sjt_craft_chosen_v1';
  var CHOSEN_AT_KEY = 'sjt_craft_chosen_at_v1';

  /*
   * PAGE VIEWS, NOT MINUTES.
   *
   * A first version expired the pin after thirty minutes of inactivity. Bed:
   * "thats too long - do u think we should change while user is browsing". He is
   * right, and the unit was wrong as well as the number.
   *
   * Wall-clock measures the wrong thing in both directions. Half an hour spent
   * browsing Ajrakh and the rail is still answering a question about Ikat; half
   * an hour with the tab open over lunch and a live ask is thrown away.
   *
   * THE PIN ONLY DOES WORK WHEN BEHAVIOUR DISAGREES WITH IT. Someone who asks
   * for Ikat and then looks at Ikat has a memory that favours Ikat already, so
   * the pin changes nothing. It is only load-bearing once they have moved on --
   * which is the moment it should stop. That makes a SHORT life safe: the cost
   * of expiring early is nil, and the cost of expiring late is the rail
   * answering a question nobody is asking any more.
   *
   * Six views, counted on the rotation cursor, which already advances exactly
   * once per product page view and is therefore the same clock the rail runs on.
   */
  var PIN_VIEWS = 6;

  function expireChoice(now) {
    try {
      var chosen = window.localStorage.getItem(CHOSEN_KEY);
      if (!chosen) return;
      var raw = window.localStorage.getItem(CHOSEN_AT_KEY);
      var at = parseInt(raw || '', 10);
      if (!isFinite(at)) {
        /* Written by craft-picks.js, which stores the craft and nothing else, or
           left over from before this existed. Stamp it with the view it was
           first seen on and let it run its six from here. */
        window.localStorage.setItem(CHOSEN_AT_KEY, String(now));
        return;
      }
      /* The cursor wraps at WRAP, so a plain subtraction can go negative on the
         one view in a thousand that straddles it. Modulo brings it back. */
      var seen = ((now - at) % WRAP + WRAP) % WRAP;
      if (seen >= PIN_VIEWS) {
        window.localStorage.removeItem(CHOSEN_KEY);
        window.localStorage.removeItem(CHOSEN_AT_KEY);
      }
    } catch (e) {
      /* Private window or storage blocked. Nothing is pinned and nothing
         expires, which is the rotation's own behaviour. */
    }
  }

  var KEY = 'sjt_craft_rotate_v1';
  /* Wraps well before any plausible number of page views, and keeps the stored
     value short. The modulo against the ranking length is what actually picks. */
  var WRAP = 1000;

  /** The cursor as it stands BEFORE this view advances it. */
  function cursor() {
    try {
      var n = parseInt(window.localStorage.getItem(KEY) || '0', 10);
      return isFinite(n) && n >= 0 ? n : 0;
    } catch (e) { return 0; }
  }

  /** This page view's position in the rotation. Advances once, on arrival. */
  function step() {
    try {
      var n = parseInt(window.localStorage.getItem(KEY) || '0', 10);
      if (!isFinite(n) || n < 0) n = 0;
      window.localStorage.setItem(KEY, String((n + 1) % WRAP));
      return n;
    } catch (e) {
      /* Private window, or storage blocked. No rotation is the old behaviour,
         which is a working rail -- never a broken one. */
      return 0;
    }
  }

  /* Before step(), so the pin is judged against the view count it was pinned on
     rather than against the one after it. */
  expireChoice(cursor());

  var at = step();

  /**
   * Rotate the ranking left by `at`, so the Nth-best craft leads on the Nth
   * page view and the order wraps round.
   *
   * Order is preserved after the rotation point, which matters: craft-picks.js
   * falls through to the next craft when the leader is on this page already or
   * has been dismissed, and it should fall through to the one after it in THEIR
   * ranking, not to a reshuffle.
   */
  function rotate(list) {
    if (!list || list.length < 2) return list;
    var k = at % list.length;
    if (!k) return list;
    return list.slice(k).concat(list.slice(0, k));
  }

  function wrap(memory) {
    if (!memory || typeof memory.top !== 'function' || memory.__rotated) return memory;
    var original = memory.top;
    memory.top = function () {
      return rotate(original.apply(memory, arguments));
    };
    /* So a second copy of this script, or a re-run, cannot wrap the wrapper and
       rotate twice in one page view. */
    memory.__rotated = true;
    return memory;
  }

  /*
   * Both scripts are deferred, so they run in document order and craft-memory.js
   * has normally defined the object by now. "Normally" is not a guarantee --
   * these snippets are rendered by different sections and the order is theirs to
   * change -- so if it is not here yet, catch the assignment instead of assuming.
   */
  if (window.SujatraCraftMemory) {
    wrap(window.SujatraCraftMemory);
  } else {
    var held;
    try {
      Object.defineProperty(window, 'SujatraCraftMemory', {
        configurable: true,
        get: function () { return held; },
        set: function (v) { held = wrap(v); },
      });
    } catch (e) { /* a rail must never break the page */ }
  }
})();